/**
 * Personal info card — first/middle/last name, dob, gender, roll, aadhar, phone, email.
 *
 * Used by both Admissions.jsx (creating drafts) and StudentEdit.jsx (editing).
 * The `idPrefix` keeps test ids unique between the two screens (e.g. `adm-` vs `edit-`).
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "./DateField";

// Numeric-only handler for phone-style fields. Allows + / - / space.
const onPhoneInput = (set, key) => (e) => set(key, e.target.value.replace(/[^\d+\-\s]/g, ""));

function NameRow({ form, set, idPrefix, requireRequired }) {
  return (
    <>
      <div>
        <Label>First name{requireRequired ? " *" : ""}</Label>
        <Input data-testid={`${idPrefix}-first-name`} required={requireRequired} value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} className="bg-white" />
      </div>
      <div>
        <Label>Middle name</Label>
        <Input data-testid={`${idPrefix}-middle-name`} value={form.middle_name || ""} onChange={(e) => set("middle_name", e.target.value)} className="bg-white" />
      </div>
      <div>
        <Label>Last name</Label>
        <Input data-testid={`${idPrefix}-last-name`} value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} className="bg-white" />
      </div>
    </>
  );
}

function DobGenderRoll({ form, set, idPrefix, requireRequired }) {
  return (
    <>
      <div>
        <Label>Date of birth{requireRequired ? " *" : ""}</Label>
        <DateField
          value={form.dob || ""}
          onChange={(iso) => set("dob", iso)}
          testId={`${idPrefix}-dob`}
          required={requireRequired}
          max={new Date().toISOString().slice(0, 10)}
          placeholder="Pick date of birth"
        />
      </div>
      <div>
        <Label>Gender</Label>
        <Select value={form.gender || "male"} onValueChange={(v) => set("gender", v)}>
          <SelectTrigger data-testid={`${idPrefix}-gender`} className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Roll number</Label>
        <Input data-testid={`${idPrefix}-roll`} value={form.roll_number || ""} onChange={(e) => set("roll_number", e.target.value)} className="bg-white font-mono" />
      </div>
    </>
  );
}

function StatusSelect({ form, set, idPrefix }) {
  return (
    <div>
      <Label>Status</Label>
      <Select value={form.status || "active"} onValueChange={(v) => set("status", v)}>
        <SelectTrigger data-testid={`${idPrefix}-status`} className="bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="alumni">Alumni / Graduated</SelectItem>
          <SelectItem value="transferred">Transferred</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function DiversityRow({ form, set, idPrefix, requireRequired }) {
  return (
    <>
      <div>
        <Label>Category{requireRequired ? " *" : ""}</Label>
        <Select value={form.category || "general"} onValueChange={(v) => set("category", v)}>
          <SelectTrigger data-testid={`${idPrefix}-category`} className="bg-white capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="obc">OBC</SelectItem>
            <SelectItem value="sc">SC</SelectItem>
            <SelectItem value="st">ST</SelectItem>
            <SelectItem value="minority">Minority</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Mother tongue{requireRequired ? " *" : ""}</Label>
        <Input
          data-testid={`${idPrefix}-mother-tongue`}
          required={requireRequired}
          value={form.mother_tongue || ""}
          onChange={(e) => set("mother_tongue", e.target.value)}
          className="bg-white"
          placeholder="Hindi, Bengali, Marathi…"
        />
      </div>
      <div>
        <Label>Physically challenged</Label>
        <Select
          value={form.physically_challenged ? "yes" : "no"}
          onValueChange={(v) => {
            const isYes = v === "yes";
            set("physically_challenged", isYes);
            if (!isYes) set("physical_challenge_type", "");
          }}
        >
          <SelectTrigger data-testid={`${idPrefix}-pc`} className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="yes">Yes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.physically_challenged && (
        <div className="md:col-span-3">
          <Label>Challenge type</Label>
          <Input
            data-testid={`${idPrefix}-pc-type`}
            value={form.physical_challenge_type || ""}
            onChange={(e) => set("physical_challenge_type", e.target.value)}
            className="bg-white"
            placeholder="Visual / Hearing / Locomotor / etc."
          />
        </div>
      )}
    </>
  );
}

function MedicalInfoRow({ form, set, idPrefix, requireRequired }) {
  return (
    <>
      <div>
        <Label>Height (cm){requireRequired ? " *" : ""}</Label>
        <Input
          data-testid={`${idPrefix}-height-cm`}
          required={requireRequired}
          type="number" min="20" max="250" step="0.1"
          value={form.height_cm ?? ""}
          onChange={(e) => set("height_cm", e.target.value === "" ? "" : Number(e.target.value))}
          className="bg-white font-mono"
          placeholder="e.g. 135"
        />
      </div>
      <div>
        <Label>Weight (kg){requireRequired ? " *" : ""}</Label>
        <Input
          data-testid={`${idPrefix}-weight-kg`}
          required={requireRequired}
          type="number" min="3" max="200" step="0.1"
          value={form.weight_kg ?? ""}
          onChange={(e) => set("weight_kg", e.target.value === "" ? "" : Number(e.target.value))}
          className="bg-white font-mono"
          placeholder="e.g. 32"
        />
      </div>
      <div>
        <Label>Blood group{requireRequired ? " *" : ""}</Label>
        <Select value={form.blood_group || ""} onValueChange={(v) => set("blood_group", v)}>
          <SelectTrigger data-testid={`${idPrefix}-blood-group`} className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"].map((bg) => (
              <SelectItem key={bg} value={bg}>{bg}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function AdmissionMetaRow({ form, set, idPrefix, requireRequired }) {
  return (
    <>
      <div>
        <Label>APAAR ID{requireRequired ? " *" : ""}</Label>
        <Input data-testid={`${idPrefix}-apaar`} required={requireRequired} value={form.apaar_id || ""} onChange={(e) => set("apaar_id", e.target.value)} className="bg-white font-mono" placeholder="12-digit APAAR" />
      </div>
      <div>
        <Label>Admission type</Label>
        <Select value={form.admission_type || "fresh"} onValueChange={(v) => set("admission_type", v)}>
          <SelectTrigger data-testid={`${idPrefix}-adm-type`} className="bg-white capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fresh">Fresh</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
            <SelectItem value="alumni_re_entry">Alumni re-entry</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Scholarship %{" "}
          <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(0–100, optional)</span>
        </Label>
        <div className="flex gap-2">
          <Input
            data-testid={`${idPrefix}-discount-pct`}
            type="number" min="0" max="100" step="1"
            value={form.discount_pct ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              set("discount_pct", v === "" ? null : Math.max(0, Math.min(100, Number(v) || 0)));
            }}
            placeholder="e.g. 35"
            className="bg-white font-mono"
          />
          <Select value="custom" onValueChange={(v) => {
            const map = { "0": 0, "25": 25, "50": 50, "75": 75, "100": 100 };
            if (v in map) set("discount_pct", map[v]);
          }}>
            <SelectTrigger data-testid={`${idPrefix}-discount-preset`} className="bg-white w-32 shrink-0"><SelectValue placeholder="Preset" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No discount</SelectItem>
              <SelectItem value="25">25%</SelectItem>
              <SelectItem value="50">50%</SelectItem>
              <SelectItem value="75">75%</SelectItem>
              <SelectItem value="100">Full waiver</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Discount reason <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(optional)</span></Label>
        <Input
          data-testid={`${idPrefix}-discount-reason`}
          value={form.discount_reason || ""}
          onChange={(e) => set("discount_reason", e.target.value)}
          placeholder="Merit scholarship, sponsor-backed…"
          className="bg-white"
        />
      </div>
      <div>
        <Label>Funded by donor ID <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(SAC sponsor, optional)</span></Label>
        <Input
          data-testid={`${idPrefix}-funded-by-donor`}
          value={form.discount_funded_by_donor_id || ""}
          onChange={(e) => set("discount_funded_by_donor_id", e.target.value)}
          placeholder="paste donor id"
          className="bg-white font-mono"
        />
        <div className="text-[10px] text-[var(--vs-text-secondary)] mt-1">When set, every paid fee period auto-attributes the discount amount to this donor for 80G impact reporting.</div>
      </div>
      <div className="md:col-span-3">
        <label className="flex items-center gap-2 text-sm">
          <input data-testid={`${idPrefix}-employee-child`} type="checkbox" checked={!!form.is_employee_child} onChange={(e) => set("is_employee_child", e.target.checked)} className="accent-[var(--vs-secondary)]" />
          Employee's child (auto-fee-waiver may apply)
        </label>
      </div>
    </>
  );
}

export function StudentInfoSection({ form, set, idPrefix = "adm", showStatus = false, requireRequired = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <NameRow form={form} set={set} idPrefix={idPrefix} requireRequired={requireRequired} />
      <DobGenderRoll form={form} set={set} idPrefix={idPrefix} requireRequired={requireRequired} />

      {showStatus && <StatusSelect form={form} set={set} idPrefix={idPrefix} />}

      <div className="md:col-span-2">
        <Label>Aadhar (encrypted){requireRequired ? " *" : ""}</Label>
        <Input data-testid={`${idPrefix}-aadhar`} required={requireRequired} value={form.aadhar || ""} onChange={(e) => set("aadhar", e.target.value)} className="bg-white font-mono" placeholder="XXXX-XXXX-XXXX" />
      </div>
      <div>
        <Label>Phone (numeric){requireRequired ? " *" : ""}</Label>
        <Input data-testid={`${idPrefix}-phone`} required={requireRequired} inputMode="numeric" pattern="[0-9+\-\s]*" value={form.phone || ""} onChange={onPhoneInput(set, "phone")} className="bg-white font-mono" />
      </div>

      <div className="md:col-span-3">
        <Label>Email{requireRequired ? " *" : <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal"> (optional)</span>}</Label>
        <Input data-testid={`${idPrefix}-email`} required={requireRequired} type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-white" placeholder="student@example.com" />
      </div>

      <MedicalInfoRow form={form} set={set} idPrefix={idPrefix} requireRequired={requireRequired} />
      <AdmissionMetaRow form={form} set={set} idPrefix={idPrefix} requireRequired={requireRequired} />
      <DiversityRow form={form} set={set} idPrefix={idPrefix} requireRequired={requireRequired} />
    </div>
  );
}
